import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  TextField,
  Typography,
} from '@shared/ui';
import { platformURL } from '@shared/utils/platformUrl';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useStartProviderOAuthMutation } from '../../store/api/apiSlice';
import AddCalendarAccountRow from './AddCalendarAccountRow';
import {
  useConnectICloudCalendarMutation,
  useDisconnectCalendarAccountMutation,
  useGetCalendarAccountsQuery,
  useUpdateConflictCalendarSelectionMutation,
} from './api/calendarSettingsApi';
import AvailabilitySettingsPanel from './availability/AvailabilitySettingsPanel';
import CalendarProviderSection from './CalendarProviderSection';
import MemberViewPanel from './member-view/MemberViewPanel';
import OutOfOfficeCard from './out-of-office/OutOfOfficeCard';
import {
  type CalendarAccountsResponse,
  type CalendarSelectionErrors,
  type CalendarSelectionOverrides,
  calendarSelectionKey,
} from './types';

const GOOGLE_PROVIDER = 'google-calendar';
const MICROSOFT_PROVIDER = 'microsoft-calendar';
const ICLOUD_PROVIDER = 'icloud-calendar';
const GOOGLE_CALENDAR_SCOPES = [
  'https://www.googleapis.com/auth/calendar.events.readonly',
  'https://www.googleapis.com/auth/calendar.readonly',
];
const MICROSOFT_CALENDAR_SCOPES = ['https://graph.microsoft.com/Calendars.Read'];
const CALENDAR_SETTINGS_REDIRECT = '/settings/calendars';

const PROVIDER_SCOPES: Record<string, string[]> = {
  [GOOGLE_PROVIDER]: GOOGLE_CALENDAR_SCOPES,
  [MICROSOFT_PROVIDER]: MICROSOFT_CALENDAR_SCOPES,
};

type CalendarSettingsSection = 'availability' | 'calendars' | 'member-view';

interface SectionConfig {
  id: CalendarSettingsSection;
  label: string;
}

const SECTION_CONFIGS: SectionConfig[] = [
  { id: 'availability', label: 'Availability' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'member-view', label: 'Member View' },
];

interface CalendarToggleArgs {
  provider: string;
  connectionId: string;
  providerCalendarId: string;
  selected: boolean;
  previousSelected: boolean;
}

interface DisconnectAccountArgs {
  provider: string;
  connectionId: string;
  accountLabel: string;
}

const CalendarSettingsPage: React.FC = () => {
  const { data, error, isLoading, isFetching } = useGetCalendarAccountsQuery();
  const [updateConflictSelection, updateConflictSelectionState] =
    useUpdateConflictCalendarSelectionMutation();
  const [disconnectCalendarAccount, disconnectCalendarAccountState] =
    useDisconnectCalendarAccountMutation();
  const [connectICloudCalendar, connectICloudCalendarState] = useConnectICloudCalendarMutation();
  const [startProviderOAuth, startGoogleOAuthState] = useStartProviderOAuthMutation();
  const [activeSection, setActiveSection] = useState<CalendarSettingsSection>('availability');
  const [selectionOverrides, setSelectionOverrides] = useState<CalendarSelectionOverrides>({});
  const [selectionErrors, setSelectionErrors] = useState<CalendarSelectionErrors>({});
  const [unsupportedProviderError, setUnsupportedProviderError] = useState(false);
  const [iCloudDialogEmail, setICloudDialogEmail] = useState<string | null>(null);

  const hasAccounts = (data?.summary.account_count ?? 0) > 0;
  const loading = isLoading || (!data && isFetching);
  const serverSelections = useMemo(() => {
    const selections: CalendarSelectionOverrides = {};
    for (const provider of data?.providers ?? []) {
      for (const account of provider.accounts) {
        for (const calendar of account.calendars) {
          selections[
            calendarSelectionKey(
              provider.provider,
              account.connection_id,
              calendar.provider_calendar_id
            )
          ] = calendar.selected_for_conflicts;
        }
      }
    }
    return selections;
  }, [data]);

  useEffect(() => {
    setSelectionOverrides((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([key, selected]) => serverSelections[key] !== selected)
      );
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [serverSelections]);

  const handleToggleCalendar = async ({
    provider,
    connectionId,
    providerCalendarId,
    selected,
    previousSelected,
  }: CalendarToggleArgs) => {
    const key = calendarSelectionKey(provider, connectionId, providerCalendarId);
    setSelectionErrors((current) => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setSelectionOverrides((current) => ({ ...current, [key]: selected }));
    try {
      await updateConflictSelection({
        provider,
        connection_id: connectionId,
        provider_calendar_id: providerCalendarId,
        selected,
      }).unwrap();
    } catch {
      setSelectionOverrides((current) => ({ ...current, [key]: previousSelected }));
      setSelectionErrors((current) => ({ ...current, [key]: Date.now() }));
    }
  };

  const handleAddAccount = async (provider: string, email?: string) => {
    setUnsupportedProviderError(false);
    if (provider === ICLOUD_PROVIDER) {
      const iCloudProvider = data?.providers.find(
        (candidate) => candidate.provider === ICLOUD_PROVIDER
      );
      if (iCloudProvider?.capabilities.connect_mode !== 'app_specific_password') {
        setUnsupportedProviderError(true);
        return;
      }
      setICloudDialogEmail(email ?? '');
      return;
    }
    const scopes = PROVIDER_SCOPES[provider];
    if (!scopes) {
      setUnsupportedProviderError(true);
      return;
    }
    try {
      const response = await startProviderOAuth({
        provider,
        scopes,
        redirect: CALENDAR_SETTINGS_REDIRECT,
      }).unwrap();
      window.location.href = response.auth_url;
    } catch {
      // RTK Query exposes mutation state; keep the page stable and show the error below.
    }
  };

  const handleDisconnectAccount = async ({
    provider,
    connectionId,
    accountLabel,
  }: DisconnectAccountArgs) => {
    const confirmed = window.confirm(`Disconnect ${accountLabel} from Calendar Settings?`);
    if (!confirmed) {
      return;
    }
    try {
      await disconnectCalendarAccount({ provider, connection_id: connectionId }).unwrap();
    } catch {
      // RTK Query exposes mutation state; keep the page stable and show the error below.
    }
  };

  return (
    <Box
      data-testid="calendar-settings-page"
      sx={{ bgcolor: '#FEFBF7', minHeight: '100vh', py: { xs: 3, md: 6 } }}
    >
      <Box sx={{ mx: 'auto', px: { xs: 2, md: 4 }, width: '100%', maxWidth: 1060 }}>
        <Box sx={{ mb: { xs: 4, md: 5 }, maxWidth: 620 }}>
          <ButtonBase
            href={platformURL('/coach/schedule')}
            sx={{ alignItems: 'center', color: '#262626', display: 'inline-flex', gap: 1.5, mb: 4 }}
          >
            <Box component="span" sx={{ fontSize: 24, lineHeight: 1 }}>
              ←
            </Box>
            <Typography sx={{ color: '#262626', fontSize: 14 }}>Back to Schedule</Typography>
          </ButtonBase>
          <Typography
            variant="h4"
            sx={{ color: '#262626', fontSize: 32, fontWeight: 700, lineHeight: 1.2, mb: 1 }}
          >
            Calendar Settings
          </Typography>
          <Typography sx={{ color: '#262626', fontSize: 16, lineHeight: 1.5 }}>
            Availability changes are automatically saved. Out-of-office changes require
            confirmation.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: '#E3E3E3', mb: { xs: 3, md: 4 } }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 4 },
            alignItems: 'flex-start',
          }}
        >
          <SectionNav activeSection={activeSection} onSelect={setActiveSection} />

          <Box sx={{ flex: 1, maxWidth: 720, width: '100%' }}>
            <Box sx={{ display: activeSection === 'availability' ? 'block' : 'none' }}>
              <AvailabilitySection />
            </Box>
            {activeSection === 'calendars' ? (
              <CalendarConflictSection
                data={data}
                error={error}
                hasAccounts={hasAccounts}
                loading={loading}
                startGoogleOAuthLoading={startGoogleOAuthState.isLoading}
                startGoogleOAuthError={startGoogleOAuthState.isError}
                unsupportedProviderError={unsupportedProviderError}
                disconnectCalendarAccountLoading={disconnectCalendarAccountState.isLoading}
                disconnectCalendarAccountError={disconnectCalendarAccountState.isError}
                updateConflictSelectionLoading={updateConflictSelectionState.isLoading}
                selectionOverrides={selectionOverrides}
                selectionErrors={selectionErrors}
                onAddAccount={(provider, email) => {
                  void handleAddAccount(provider, email);
                }}
                onDisconnectAccount={(args) => {
                  void handleDisconnectAccount(args);
                }}
                onToggleCalendar={(args) => {
                  void handleToggleCalendar(args);
                }}
              />
            ) : null}
            {activeSection === 'member-view' ? <MemberViewPanel /> : null}
          </Box>
        </Box>
      </Box>
      {iCloudDialogEmail !== null ? (
        <ICloudConnectDialog
          initialEmail={iCloudDialogEmail}
          submitting={connectICloudCalendarState.isLoading}
          onClose={() => setICloudDialogEmail(null)}
          onSubmit={async (request) => {
            await connectICloudCalendar(request).unwrap();
            setICloudDialogEmail(null);
          }}
        />
      ) : null}
    </Box>
  );
};

interface SectionNavProps {
  activeSection: CalendarSettingsSection;
  onSelect: (section: CalendarSettingsSection) => void;
}

const SectionNav: React.FC<SectionNavProps> = ({ activeSection, onSelect }) => (
  <Box
    role="tablist"
    aria-label="Calendar settings sections"
    sx={{
      display: 'flex',
      flexDirection: { xs: 'row', md: 'column' },
      gap: 1,
      width: { xs: '100%', md: 220 },
      flexShrink: 0,
    }}
  >
    {SECTION_CONFIGS.map((section) => (
      <SectionNavButton
        key={section.id}
        section={section}
        selected={activeSection === section.id}
        onSelect={onSelect}
      />
    ))}
  </Box>
);

interface SectionNavButtonProps {
  section: SectionConfig;
  selected: boolean;
  onSelect: (section: CalendarSettingsSection) => void;
}

const SectionNavButton: React.FC<SectionNavButtonProps> = ({ section, selected, onSelect }) => (
  <ButtonBase
    id={`calendar-settings-section-${section.id}`}
    role="tab"
    aria-selected={selected}
    aria-controls={`calendar-settings-${section.id}-section`}
    data-testid={`calendar-settings-section-${section.id}`}
    onClick={() => onSelect(section.id)}
    sx={{
      bgcolor: selected ? '#FBF5EC' : 'transparent',
      borderRadius: 2,
      color: '#262626',
      fontSize: 16,
      fontWeight: selected ? 500 : 400,
      justifyContent: 'flex-start',
      px: 1.5,
      py: 1,
      textAlign: 'left',
      width: { xs: 'auto', md: '100%' },
    }}
  >
    {section.label}
  </ButtonBase>
);

const AvailabilitySection: React.FC = () => (
  <Box
    id="calendar-settings-availability-section"
    role="tabpanel"
    aria-labelledby="calendar-settings-section-availability"
  >
    <AvailabilitySettingsPanel />
    <OutOfOfficeCard />
  </Box>
);

interface CalendarConflictSectionProps {
  data?: CalendarAccountsResponse;
  error: unknown;
  hasAccounts: boolean;
  loading: boolean;
  startGoogleOAuthLoading: boolean;
  startGoogleOAuthError: boolean;
  unsupportedProviderError: boolean;
  disconnectCalendarAccountLoading: boolean;
  disconnectCalendarAccountError: boolean;
  updateConflictSelectionLoading: boolean;
  selectionOverrides: CalendarSelectionOverrides;
  selectionErrors: CalendarSelectionErrors;
  onAddAccount: (provider: string, email?: string) => void;
  onDisconnectAccount: (args: DisconnectAccountArgs) => void;
  onToggleCalendar: (args: CalendarToggleArgs) => void;
}

const CalendarConflictSection: React.FC<CalendarConflictSectionProps> = ({
  data,
  error,
  hasAccounts,
  loading,
  startGoogleOAuthLoading,
  startGoogleOAuthError,
  unsupportedProviderError,
  disconnectCalendarAccountLoading,
  disconnectCalendarAccountError,
  updateConflictSelectionLoading,
  selectionOverrides,
  selectionErrors,
  onAddAccount,
  onDisconnectAccount,
  onToggleCalendar,
}) => (
  <Box
    id="calendar-settings-calendars-section"
    role="tabpanel"
    aria-labelledby="calendar-settings-section-calendars"
  >
    <Card
      variant="outlined"
      sx={{ bgcolor: 'transparent', borderColor: '#E3E3E3', borderRadius: 2, mb: 3 }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: '#262626', fontSize: 16, fontWeight: 700, lineHeight: 1.5 }}
          >
            Search for Conflicts
          </Typography>
          <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
            Select which of your calendars Mento should read for meeting conflicts.
          </Typography>
        </Box>

        <Divider sx={{ borderColor: '#E3E3E3', mb: 2.5 }} />

        {loading ? (
          <Box
            data-testid="calendar-settings-loading-placeholder"
            sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading calendar accounts…
            </Typography>
          </Box>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            We couldn’t load calendar accounts. Refresh the page or try again later.
          </Alert>
        ) : null}

        {startGoogleOAuthError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            We couldn’t start calendar account linking. Please try again.
          </Alert>
        ) : null}

        {unsupportedProviderError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            That calendar provider is not supported for account linking yet.
          </Alert>
        ) : null}

        {disconnectCalendarAccountError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            We couldn’t disconnect that calendar account. Please try again.
          </Alert>
        ) : null}

        {data && hasAccounts ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {data.providers.map((provider) => (
              <CalendarProviderSection
                key={provider.provider}
                provider={provider}
                mutationPending={updateConflictSelectionLoading || disconnectCalendarAccountLoading}
                onReconnect={onAddAccount}
                onDisconnectAccount={onDisconnectAccount}
                selectionOverrides={selectionOverrides}
                selectionErrors={selectionErrors}
                onToggleCalendar={onToggleCalendar}
              />
            ))}
            <AddCalendarAccountRow
              disabled={startGoogleOAuthLoading}
              onAdd={onAddAccount}
              availableProviders={data.providers.map((provider) => provider.provider)}
            />
          </Box>
        ) : null}

        {data && !hasAccounts && !error ? (
          <Box data-testid="calendar-settings-empty-placeholder">
            <Alert severity="info" sx={{ mb: 3 }}>
              No calendar accounts connected yet. Add a Google Calendar account to discover
              calendars for conflict checks.
            </Alert>
            <AddCalendarAccountRow
              disabled={startGoogleOAuthLoading}
              onAdd={onAddAccount}
              availableProviders={data.providers.map((provider) => provider.provider)}
            />
          </Box>
        ) : null}
      </CardContent>
    </Card>
  </Box>
);

const APPLE_ACCOUNT_URL = 'https://account.apple.com';
const APPLE_APP_SPECIFIC_PASSWORD_HELP_URL = 'https://support.apple.com/102654';

interface ICloudConnectDialogProps {
  initialEmail: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (request: { email: string; app_specific_password: string }) => Promise<void>;
}

function iCloudConnectErrorMessage(error: unknown): string {
  const code = (error as { data?: { error?: string } } | undefined)?.data?.error;
  if (code === 'calendar_connect_invalid') {
    return 'Enter your Apple Account email and app-specific password.';
  }
  if (code === 'calendar_credentials_rejected') {
    return 'Apple didn’t accept that email and app-specific password. Check both and try again. Use an app-specific password, not your Apple Account password.';
  }
  return 'We couldn’t reach iCloud to check that account. Please try again.';
}

const ICloudConnectDialog: React.FC<ICloudConnectDialogProps> = ({
  initialEmail,
  submitting,
  onClose,
  onSubmit,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [appSpecificPassword, setAppSpecificPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    try {
      await onSubmit({ email: email.trim(), app_specific_password: appSpecificPassword.trim() });
    } catch (error) {
      setErrorMessage(iCloudConnectErrorMessage(error));
    }
  };

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="icloud-connect-dialog-title"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle id="icloud-connect-dialog-title">Connect iCloud Calendar</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography sx={{ color: '#505A54', fontSize: 13, lineHeight: 1.5 }}>
            Mento reads your iCloud calendars for conflicts using an app-specific password. Never
            enter your Apple Account password here.
          </Typography>
          <Box
            component="ol"
            sx={{ color: '#505A54', fontSize: 13, lineHeight: 1.6, m: 0, pl: 2.5 }}
          >
            <li>
              Sign in at{' '}
              <Link href={APPLE_ACCOUNT_URL} target="_blank" rel="noreferrer">
                account.apple.com
              </Link>
              .
            </li>
            <li>Open Sign-In and Security, then App-Specific Passwords.</li>
            <li>Create a password named “Mento” and paste it below.</li>
          </Box>
          <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
            Your Apple Account needs two-factor authentication. Accounts managed by an employer or
            school can’t create app-specific passwords.{' '}
            <Link href={APPLE_APP_SPECIFIC_PASSWORD_HELP_URL} target="_blank" rel="noreferrer">
              Apple’s help
            </Link>
          </Typography>
          <TextField
            autoFocus={initialEmail === ''}
            label="Apple Account email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            fullWidth
          />
          <TextField
            autoFocus={initialEmail !== ''}
            label="App-specific password"
            type="password"
            value={appSpecificPassword}
            onChange={(event) => setAppSpecificPassword(event.target.value)}
            required
            fullWidth
            placeholder="xxxx-xxxx-xxxx-xxxx"
          />
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="text" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            Connect iCloud Calendar
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CalendarSettingsPage;
